import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@atoms/Button';
import { Input } from '@atoms/Input';
import { Card } from '@atoms/Card';
import { Spinner } from '@atoms/Spinner';
import { geoService } from '@services/geoService';
import { formatCurrency } from '@utils/formatters';
import { ServiceAreaMap } from '@molecules/ServiceAreaMap';

import type { ServiceArea } from '@/types';

const DAY_LABELS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

export function ServiceAreasSection() {
  const queryClient = useQueryClient();
  const [polygon, setPolygon] = useState<number[][]>([]);
  const [form, setForm] = useState({
    city: 'ADANA',
    district: 'ALADAĞ',
    neighborhood: '',
    minOrderTotal: '',
    deliveryFee: '',
    freeDeliveryThreshold: '',
    deliveryHours: '00:00-23:59',
    deliveryDays: '0,1,2,3,4,5,6',
  });

  const { data: areas, isLoading } = useQuery({
    queryKey: ['wholesaler', 'service-areas'],
    queryFn: () => geoService.getServiceAreas(),
  });

  const { data: neighborhoods, isFetching: neighborhoodsLoading } = useQuery({
    queryKey: ['neighborhoods', form.city, form.district],
    queryFn: () => geoService.getNeighborhoods(form.city, form.district),
    enabled: form.city.length > 1 && form.district.length > 1,
  });

  useEffect(() => {
    if (neighborhoods?.length && !form.neighborhood) {
      setForm((prev) => ({ ...prev, neighborhood: neighborhoods[0] }));
    }
  }, [neighborhoods, form.neighborhood]);

  const createMutation = useMutation({
    mutationFn: () =>
      geoService.createServiceArea({
        city: form.city,
        district: form.district,
        neighborhood: form.neighborhood,
        minOrderTotal: Number(form.minOrderTotal || 0),
        deliveryFee: Number(form.deliveryFee || 0),
        freeDeliveryThreshold: form.freeDeliveryThreshold
          ? Number(form.freeDeliveryThreshold)
          : undefined,
        deliveryDays: form.deliveryDays,
        deliveryHours: form.deliveryHours,
        polygonJson: polygon.length >= 3 ? polygon : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wholesaler', 'service-areas'] });
      setForm((prev) => ({
        ...prev,
        neighborhood: '',
        minOrderTotal: '',
        deliveryFee: '',
        freeDeliveryThreshold: '',
      }));
      setPolygon([]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => geoService.deleteServiceArea(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wholesaler', 'service-areas'] }),
  });

  const scheduleMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ServiceArea> }) =>
      geoService.updateSchedule(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wholesaler', 'service-areas'] }),
  });

  const toggleDay = (dayIndex: number) => {
    const days = new Set(form.deliveryDays.split(',').map((d) => d.trim()).filter(Boolean));
    const key = String(dayIndex);
    if (days.has(key)) days.delete(key);
    else days.add(key);
    setForm({ ...form, deliveryDays: [...days].sort((a, b) => Number(a) - Number(b)).join(',') });
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Hizmet Bölgeleri</h2>
      <Card className="grid gap-3 md:grid-cols-3">
        <Input label="İl" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value.toUpperCase(), neighborhood: '' })} />
        <Input label="İlçe" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value.toUpperCase(), neighborhood: '' })} />
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-gray-700">Mahalle</span>
          <select
            value={form.neighborhood}
            onChange={(e) => setForm({ ...form, neighborhood: e.target.value })}
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
            disabled={neighborhoodsLoading}
          >
            <option value="">{neighborhoodsLoading ? 'Yükleniyor...' : 'Seçin'}</option>
            {(neighborhoods ?? []).map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <Input label="Min. Sipariş" type="number" value={form.minOrderTotal} onChange={(e) => setForm({ ...form, minOrderTotal: e.target.value })} />
        <Input label="Teslimat Ücreti" type="number" value={form.deliveryFee} onChange={(e) => setForm({ ...form, deliveryFee: e.target.value })} />
        <Input label="Ücretsiz Teslimat Eşiği" type="number" value={form.freeDeliveryThreshold} onChange={(e) => setForm({ ...form, freeDeliveryThreshold: e.target.value })} />
        <Input label="Teslimat Saatleri" value={form.deliveryHours} onChange={(e) => setForm({ ...form, deliveryHours: e.target.value })} placeholder="08:00-20:00" />
        <div className="md:col-span-2">
          <p className="mb-2 text-sm font-medium text-gray-700">Teslimat Günleri</p>
          <div className="flex flex-wrap gap-2">
            {DAY_LABELS.map((label, index) => (
              <button
                key={label}
                type="button"
                onClick={() => toggleDay(index)}
                className={`rounded-full px-3 py-1 text-sm ${
                  form.deliveryDays.split(',').includes(String(index))
                    ? 'bg-brand-500 text-white'
                    : 'bg-gray-100'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-end">
          <Button
            loading={createMutation.isPending}
            disabled={!form.city || !form.district || !form.neighborhood}
            onClick={() => createMutation.mutate()}
          >
            Ekle
          </Button>
        </div>
      </Card>
      <Card>
        <h3 className="mb-3 font-medium">Harita (Polygon — opsiyonel)</h3>
        <ServiceAreaMap polygon={polygon} onPolygonChange={setPolygon} />
      </Card>
      <div className="grid gap-3 md:grid-cols-2">
        {(areas ?? []).map((area) => (
          <Card key={area.id} className="space-y-2">
            <div className="flex justify-between gap-3">
              <div>
                <p className="font-medium">{area.neighborhood}, {area.district}</p>
                <p className="text-sm text-gray-500">{area.city}</p>
                <p className="text-xs text-gray-500">
                  Min: {formatCurrency(area.minOrderTotal)} · Teslimat: {formatCurrency(area.deliveryFee)}
                </p>
                <p className="text-xs text-gray-400">
                  {area.deliveryHours} · Günler: {area.deliveryDays}
                  {area.polygonJson?.length ? ` · ${area.polygonJson.length} nokta polygon` : ''}
                </p>
              </div>
              <Button size="sm" variant="danger" onClick={() => deleteMutation.mutate(area.id)}>Sil</Button>
            </div>
            <div className="flex gap-2">
              <Input
                label="Saat"
                value={area.deliveryHours ?? ''}
                onChange={(e) =>
                  scheduleMutation.mutate({
                    id: area.id,
                    data: { ...area, deliveryHours: e.target.value },
                  })
                }
              />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
